import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Play, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg } from "@/lib/api";
import { PageHead, card, Field, inputCls, Sel, PatientSelect } from "@/components/admin/kit";
import { ScriptBox } from "@/components/admin/CallPreview";
import { VoiceSettings } from "@/components/admin/VoiceLibrary";

function WordMap({ title, value, onChange, idp }) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase text-muted-foreground">{title}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(value || {}).map(([k, v]) => (
          <label key={k} className="flex items-center gap-2 text-xs"><span className="w-24 shrink-0 font-semibold">{k}</span>
            <input className={`${inputCls} font-te`} value={v} onChange={(e) => onChange({ ...value, [k]: e.target.value })} data-testid={`${idp}-${k}`} /></label>
        ))}
      </div>
    </div>
  );
}

function NestedMap({ title, value, fields, onChange, idp }) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase text-muted-foreground">{title}</p>
      <div className="grid gap-2">
        {Object.entries(value || {}).map(([k, v]) => (
          <div key={k} className="grid items-center gap-2 text-xs" style={{ gridTemplateColumns: `110px repeat(${fields.length}, minmax(0,1fr))` }}><b>{k}</b>
            {fields.map((fld) => <input key={fld} className={`${inputCls} font-te`} value={(typeof v === "object" ? v[fld] : v) || ""} placeholder={fld} onChange={(e) => onChange({ ...value, [k]: { ...(typeof v === "object" ? v : {}), [fld]: e.target.value } })} data-testid={`${idp}-${k}-${fld}`} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

const TAGS = { family_alert: "Family alerts · off unless switched on in Settings", stop_alert: "Family alerts · off unless switched on in Settings", health_update: "WhatsApp text only — never in a call" };

const KINDS = [["daily", "Dose reminder"], ["welcome", "Welcome"], ["refill", "Refill"], ["followup", "Follow-up"], ["dental", "Dental"], ["stop_confirm", "Stop confirmation"]];
const SLOT_OPTS = [["morning", "Morning"], ["afternoon", "Afternoon"], ["evening", "Evening"], ["night", "Night"]];

function wordDiff(a, b) {
  const x = a.split(/(\s+)/), y = b.split(/(\s+)/);
  const m = x.length, n = y.length;
  const L = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) L[i][j] = x[i] === y[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && x[i] === y[j]) { out.push(["same", x[i]]); i++; j++; }
    else if (j < n && (i >= m || L[i][j + 1] >= L[i + 1][j])) { out.push(["add", y[j]]); j++; }
    else { out.push(["del", x[i]]); i++; }
  }
  return out;
}

function Diff({ now, then, testid }) {
  const parts = wordDiff(now, then);
  if (now === then) return <p className="text-xs text-muted-foreground" data-testid={testid}>Same as today's text.</p>;
  return (
    <p className="font-te leading-relaxed" data-testid={testid}>
      {parts.map(([t, w], k) => t === "same" ? <span key={k}>{w}</span>
        : t === "del" ? <del key={k} className="mx-px rounded bg-red-100 px-0.5 text-red-700">{w}</del>
          : <ins key={k} className="mx-px rounded bg-green-100 px-0.5 text-green-800 no-underline">{w}</ins>)}
    </p>
  );
}

function Versions({ t, reload }) {
  const api = useApi();
  const [list] = useLoad(`/templates/${t.id}/versions`);
  if (!list?.length) return <p className="text-xs text-muted-foreground">No earlier versions yet.</p>;
  return (
    <>
      <p className="text-xs text-muted-foreground">What changes if you go back: <del className="rounded bg-red-100 px-1 text-red-700">removed from today's text</del> <ins className="rounded bg-green-100 px-1 text-green-800 no-underline">comes back</ins></p>
      {list.map((v) => (
        <div key={v.id} className="grid gap-1 border-t py-2 text-xs" data-testid={`template-version-${v.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">Version used until {new Date(v.replaced_at).toLocaleString("en-IN")}</span>
            <Button size="sm" variant="outline" className="ml-auto" disabled={v.text === t.text} onClick={async () => { if (!window.confirm("Go back to this version? It applies to all future calls. Today's text is kept as a version.")) return; try { await api.post(`/templates/${t.id}/restore/${v.id}`); toast.success("Version restored"); reload(); } catch (e) { toast.error(errMsg(e)); } }} data-testid={`template-restore-${v.id}`}>Go back to this</Button>
          </div>
          <Diff now={t.text} then={v.text} testid={`template-diff-${v.id}`} />
        </div>
      ))}
    </>
  );
}

function TemplateRow({ t, reload }) {
  const api = useApi();
  const [text, setText] = useState(t.text);
  const [pv, setPv] = useState({ patient_id: "", kind: "daily", slot: "morning" });
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [hist, setHist] = useState(false);
  const dirty = text !== t.text;
  const preview = async (audio) => {
    if (!pv.patient_id) return toast.error("Choose a sample patient for the preview");
    setBusy(audio ? "play" : "show");
    try { const r = await api.post(`/templates/${t.id}/preview`, { ...pv, text, audio }); setS(r.data); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const save = async () => {
    if (!window.confirm("Apply to all future calls? Every call made from now on uses this wording. The old text is kept as a version you can go back to.")) return;
    try { await api.put(`/templates/${t.id}`, { text }); toast.success("Saved — applies to all future calls"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-[var(--lav-3)] p-3" data-testid={`template-${t.key}`}>
      <div className="flex flex-wrap items-center gap-2"><b className="text-sm">{t.key}</b>{TAGS[t.key] && <span className="rounded-full bg-orange-100 px-2 text-[11px] font-semibold text-orange-800" data-testid={`template-tag-${t.key}`}>{TAGS[t.key]}</span>}{(t.placeholders || []).map((p) => <code key={p} className="rounded bg-[var(--lav-2)] px-1.5 text-[11px]">{`{${p}}`}</code>)}
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setHist(!hist)} data-testid={`template-history-${t.key}`}>Versions</Button>
        <Button size="sm" disabled={!dirty} onClick={save} data-testid={`template-save-${t.key}`}>Save</Button></div>
      <textarea className={`${inputCls} font-te mt-2 min-h-[70px]`} value={text} onChange={(e) => setText(e.target.value)} data-testid={`template-text-${t.key}`} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <div className="w-56"><PatientSelect value={pv.patient_id} onChange={(v) => setPv({ ...pv, patient_id: v })} testid={`template-pv-patient-${t.key}`} /></div>
        <div className="w-40"><Sel value={pv.kind} onChange={(v) => setPv({ ...pv, kind: v })} options={KINDS} testid={`template-pv-kind-${t.key}`} /></div>
        {pv.kind === "daily" && <div className="w-32"><Sel value={pv.slot} onChange={(v) => setPv({ ...pv, slot: v })} options={SLOT_OPTS} testid={`template-pv-slot-${t.key}`} /></div>}
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => preview(false)} data-testid={`template-preview-${t.key}`}>Preview</Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => preview(true)} data-testid={`template-play-${t.key}`}><Play size={14} /> {busy === "play" ? "Making audio…" : "Play (phone quality)"}</Button>
      </div>
      {s && <div className="mt-2"><p className="mb-1 text-xs text-muted-foreground">{dirty ? "Preview with your unsaved text" : "Preview with the saved text"} (only shows a change if this template is used for that patient and call)</p><ScriptBox s={s} testid={`template-pv-script-${t.key}`} /></div>}
      {hist && <div className="mt-2"><Versions t={t} reload={reload} /></div>}
    </div>
  );
}

function VoiceTest({ lang }) {
  const api = useApi();
  const [text, setText] = useState("");
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const body = { text, language: lang.code, voice: lang.voice, pace: lang.pace ?? 1, loudness: lang.loudness ?? 1, pitch: lang.pitch ?? 0, sample_rate: lang.sample_rate ?? 24000 };
      const r = await api.post("/voice/test", body, { responseType: "blob" });
      setUrl(URL.createObjectURL(r.data));
    } catch (e) {
      let m = errMsg(e);
      if (e?.response?.data instanceof Blob) { try { m = JSON.parse(await e.response.data.text()).detail; } catch {} }
      toast.error(m);
    } finally { setBusy(false); }
  };
  return (
    <div className={`${card} mt-4 grid gap-3`} data-testid="voice-test">
      <p className="font-bold">Voice test <span className="text-xs font-normal text-muted-foreground">— like “Run request” in the Sarvam playground: paste text and hear it with the voice and settings above (even before saving).</span></p>
      <textarea className={`${inputCls} font-te min-h-[110px]`} maxLength={2500} value={text} onChange={(e) => setText(e.target.value)} data-testid="voice-test-text" />
      <div className="flex flex-wrap items-end gap-3">
        <span className="text-sm" data-testid="voice-test-settings">{lang.voice} · pace {lang.pace ?? 1} · loudness {lang.loudness ?? 1} · pitch {lang.pitch ?? 0}</span>
        <Button onClick={run} disabled={!text.trim() || busy} data-testid="voice-test-btn">{busy ? "Generating…" : "Run request"}</Button>
        {url && <audio controls autoPlay src={url} className="h-10" data-testid="voice-test-audio" />}
        <span className="text-xs text-muted-foreground">{text.length}/2500</span>
      </div>
    </div>
  );
}

function SlotVoices({ lang, setLang }) {
  const api = useApi();
  const [voices] = useLoad("/voices");
  const [busy, setBusy] = useState("");
  const sv = lang.slot_voices || {};
  const play = async (slot, phone = false) => {
    setBusy(phone ? `${slot}-phone` : slot);
    try {
      const r = await api.post("/voices/preview", { voice: sv[slot] || lang.voice, language: lang.code, phone }, { responseType: "blob" });
      new Audio(URL.createObjectURL(r.data)).play();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-2xl bg-[var(--lav)] p-4" data-testid="slot-voices">
      <p className="font-bold">Voice by time slot</p>
      <p className="text-xs text-muted-foreground">Same voice for the same slot every day. Refill, welcome, check-up and dental calls use the Morning voice. A patient can override this in their record. <Play size={11} className="inline" /> plays full quality, <Phone size={11} className="inline" /> plays it as heard on a real phone call (8 kHz).</p>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {["morning", "afternoon", "night"].map((sl) => (
          <Field key={sl} label={sl[0].toUpperCase() + sl.slice(1)}>
            <div className="flex gap-2">
              <Sel value={sv[sl] || lang.voice} onChange={(v) => setLang({ ...lang, slot_voices: { ...sv, [sl]: v } })} options={(voices || []).map((v) => [v.id, `${v.name} · ${v.use_case} · ${v.gender}`])} testid={`slot-voice-${sl}`} />
              <Button type="button" variant="outline" size="icon" onClick={() => play(sl)} disabled={!!busy} aria-label={`Play ${sl} voice`} data-testid={`slot-voice-play-${sl}`}>{busy === sl ? "…" : <Play size={14} />}</Button>
              <Button type="button" variant="outline" size="icon" onClick={() => play(sl, true)} disabled={!!busy} aria-label={`Play ${sl} voice in phone quality`} title="Phone quality (8 kHz, as on a real call)" data-testid={`slot-voice-phone-${sl}`}>{busy === `${sl}-phone` ? "…" : <Phone size={14} />}</Button>
            </div>
          </Field>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Press “Save language settings” below to keep changes.</p>
    </div>
  );
}

export default function Templates() {
  const api = useApi();
  const [langs, reloadLangs] = useLoad("/languages");
  const [code, setCode] = useState("te-IN");
  const [tpls, reloadT] = useLoad(`/templates?language=${code}`);
  const [lang, setLang] = useState(null);
  const [nl, setNl] = useState(null);
  useEffect(() => { setLang((langs || []).find((l) => l.code === code) || null); }, [langs, code]);

  const saveLang = async () => {
    try { await api.put(`/languages/${code}`, lang); toast.success("Language saved"); reloadLangs(); } catch (e) { toast.error(errMsg(e)); }
  };
  const addLang = async (e) => {
    e.preventDefault();
    try { await api.post("/languages", nl); setNl(null); reloadLangs(); setCode(nl.code); toast.success("Language added — fill its templates, then activate"); } catch (err) { toast.error(errMsg(err)); }
  };
  const forms = lang?.form_words || {};
  return (
    <div data-testid="templates-page">
      <PageHead title="Templates & languages" sub="All call wording lives here as data. Adding a language = new templates + a voice choice. No code changes.">
        <div className="w-48"><Sel value={code} onChange={setCode} options={(langs || []).map((l) => [l.code, `${l.name} (${l.code})${l.active ? "" : " · inactive"}`])} testid="lang-select" /></div>
        <Button variant="outline" onClick={() => setNl({ code: "hi-IN", name: "Hindi", native_name: "हिन्दी", voice: "", greeting_word: "जी" })} data-testid="add-language-btn"><Plus size={16} /> Add language</Button>
      </PageHead>
      {lang && (
        <div className={`${card} grid gap-5`} data-testid="language-settings">
          <VoiceSettings lang={lang} setLang={setLang} />
          <SlotVoices lang={lang} setLang={setLang} />
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Respectful greeting word"><input className={`${inputCls} font-te`} value={lang.greeting_word} onChange={(e) => setLang({ ...lang, greeting_word: e.target.value })} data-testid="lang-greeting-input" /></Field>
            <Field label="Speech speed (characters / second)" hint="Used for the 60-second estimate"><input type="number" className={inputCls} value={lang.chars_per_second} onChange={(e) => setLang({ ...lang, chars_per_second: Number(e.target.value) })} data-testid="lang-cps-input" /></Field>
            <label className="flex items-center gap-2 self-end pb-2 text-sm font-semibold"><input type="checkbox" checked={lang.active} onChange={(e) => setLang({ ...lang, active: e.target.checked })} data-testid="lang-active-checkbox" /> Active</label>
          </div>
          <WordMap title="Time slot words" value={lang.slot_words} onChange={(v) => setLang({ ...lang, slot_words: v })} idp="slot-word" />
          <NestedMap title="Food instruction words — per time slot" value={lang.food_words} fields={["morning", "afternoon", "night"]} onChange={(v) => setLang({ ...lang, food_words: v })} idp="food-word" />
          <WordMap title="Purpose words" value={lang.purpose_words} onChange={(v) => setLang({ ...lang, purpose_words: v })} idp="purpose-word" />
          <WordMap title="Dose number words (1 → ఒక, 0.5 → అర)" value={lang.number_words} onChange={(v) => setLang({ ...lang, number_words: v })} idp="number-word" />
          <NestedMap title={"Medicine forms — name · plural · one · many ({n} = number word) · verb · verb when calling a caregiver"} value={forms} fields={["name", "plural_name", "one", "many", "verb", "verb_caregiver"]} onChange={(v) => setLang({ ...lang, form_words: v })} idp="form-word" />
          <Button onClick={saveLang} className="justify-self-start" data-testid="lang-save-btn">Save language settings</Button>
        </div>
      )}
      {lang && <VoiceTest lang={lang} />}
      <div className="mt-4 grid gap-3">{(tpls || []).map((t) => <TemplateRow key={t.id + t.updated_at} t={t} reload={reloadT} />)}</div>
      <Dialog open={!!nl} onOpenChange={(o) => !o && setNl(null)}>
        <DialogContent><DialogHeader><DialogTitle>Add language</DialogTitle></DialogHeader>
          {nl && (
            <form onSubmit={addLang} className="grid gap-3" data-testid="add-language-form">
              {[["code", "Code (e.g. hi-IN)"], ["name", "Name"], ["native_name", "Native name"], ["voice", "Sarvam voice"], ["greeting_word", "Greeting word"]].map(([k, l]) => (
                <Field key={k} label={l}><input className={inputCls} value={nl[k]} onChange={(e) => setNl({ ...nl, [k]: e.target.value })} data-testid={`new-lang-${k}`} /></Field>
              ))}
              <Button data-testid="new-lang-save">Add (starts inactive with empty templates)</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
